// What is on a machine RIGHT NOW, as table rows.
//
// Execution History and the Drum Ledger are both built from ExecutionLogs, and
// an ExecutionLog is written when a drum FINISHES. So both stop at the last
// completed drum, and the product actually turning on the line is absent —
// which is very often the product a mis-recorded drum belongs to. You could see
// the evidence and not the thing it pointed at.
//
// These rows are NOT history. They are the present, they change under you, and
// the MACHINE owns their lengths: `unifiedWorker` assigns `producedLength` from
// the PLC counter on every loop (`logicalProducedNow = virt − lastRolloverVirt`,
// then `LUA_TICK_UPDATE` does `tok.producedLength = produced` — an assignment,
// not an accumulation). Anything written to them from a screen is overwritten
// within a second, silently. So they are context and a DESTINATION, never a
// target, and the screens render them without actions.
//
// Shared because two screens show the same thing and a second copy would drift.

/** Statuses that mean the drum is finished and therefore already history. */
const DONE = ['DONE', 'FINISHED', 'COMPLETED'];

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * @param queueData the body of GET /api/redis/queues/:machineId
 * @returns one row per not-yet-finished output drum, running ones first
 */
export function buildLiveDrumRows(queueData) {
  const rows = [];

  for (const group of queueData?.groups || []) {
    for (const task of group?.queue || []) {
      for (const out of task?.outputs || []) {
        if (!out?.tokenId) continue;
        const status = String(out.status || task.status || 'PENDING').toUpperCase();
        // A finished drum is history and is already in the table; listing it
        // twice would make the run read as longer than it was.
        if (DONE.includes(status)) continue;

        rows.push({
          key: `${group.jobId}:${out.tokenId}`,
          jobId: String(group.jobId),
          jobCode: group.jobCode || null,
          productLabel: group.productLabel || group.name || String(group.jobId),
          phase: task.phase || out.phase || null,
          taskId: task.taskId || null,
          tokenId: out.tokenId,
          color: out.color ?? task.color ?? null,
          // The drum's OWN planned length, not the task's: a grouped or tandem
          // task carries several drums and its plannedLength is their sum.
          plannedLength: num(out.plannedLength ?? task.plannedLength),
          producedLength: num(out.producedLength),
          progress: num(out.progress),
          status,
          drum_material: out.drum_material ?? task.drum_material ?? null,
          drum_diameter: out.drum_diameter ?? task.drum_diameter ?? null,
          live: true
        });
      }
    }
  }

  // Running first — it is the drum the operator is standing at.
  rows.sort((a, b) => (b.status === 'ACTIVE' ? 1 : 0) - (a.status === 'ACTIVE' ? 1 : 0));
  return rows;
}

/** How full the drum is, for the progress bar. 0 when nothing is planned. */
export function liveDrumPercent(row) {
  const planned = num(row?.plannedLength);
  if (planned <= 0) return 0;
  return Math.min(100, Math.round((num(row.producedLength) / planned) * 100));
}

/**
 * Did somebody actually type a length?
 *
 * `Number('')` is 0 and 0 is a finite number, so an untouched field reads as
 * "this drum held nothing" — which made an untouched row in the Drum Ledger
 * announce that its whole 6 344 m were moving to the next drum. An explicit 0
 * is a legitimate answer ("this drum was never this product's"), so only the
 * ABSENCE is rejected. Same rule the backend applies in
 * redis_dev/helpers/executionSplit.js.
 */
export function typedLength(value) {
  if (value === null || value === undefined) return NaN;
  const s = String(value).trim();
  if (s === '') return NaN;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
}
